/**
 * The virtual screen every scene draws into (docs/spec/04 §1): 224 x 288, the arcade's portrait
 * monitor, 28 columns by 36 rows of text. The display pass scales it to the window.
 */

import { context2d, makeCanvas } from './canvas';
import type { Colour } from './font';
import { css, drawText, GLYPH, textWidth } from './font';

export type Align = 'left' | 'center' | 'right';

export const SCREEN_W = 224;
export const SCREEN_H = 288;

export class Screen {
  readonly canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;

  constructor(width = SCREEN_W, height = SCREEN_H) {
    this.canvas = makeCanvas(width, height);
    this.ctx = context2d(this.canvas);
  }

  get width(): number {
    return this.canvas.width;
  }

  get height(): number {
    return this.canvas.height;
  }

  /** Change the virtual size (clears it). */
  resize(width: number, height: number): void {
    if (width === this.canvas.width && height === this.canvas.height) return;
    this.canvas.width = width;
    this.canvas.height = height;
    this.ctx = context2d(this.canvas);
  }

  clear(colour: Colour = 0): void {
    this.fill(0, 0, this.width, this.height, colour);
  }

  fill(x: number, y: number, w: number, h: number, colour: Colour): void {
    this.ctx.fillStyle = css(colour);
    this.ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
  }

  /** A one-pixel outline inside the rectangle. */
  box(x: number, y: number, w: number, h: number, colour: Colour): void {
    this.fill(x, y, w, 1, colour);
    this.fill(x, y + h - 1, w, 1, colour);
    this.fill(x, y, 1, h, colour);
    this.fill(x + w - 1, y, 1, h, colour);
  }

  /** Text at (x, y); `align` is about x. */
  text(s: string, x: number, y: number, colour: Colour, align: Align = 'left', scale = 1): void {
    const w = textWidth(s, scale);
    const left = align === 'center' ? x - w / 2 : align === 'right' ? x - w : x;
    drawText(this.ctx, s, Math.round(left), y, colour, scale);
  }

  /** Text with a one-pixel drop shadow, for text over busy backgrounds. */
  shadowText(s: string, x: number, y: number, colour: Colour, shadow: Colour = 0, align: Align = 'left', scale = 1): void {
    this.text(s, x + scale, y + scale, shadow, align, scale);
    this.text(s, x, y, colour, align, scale);
  }

  /** Text on the 28-column character grid, as the arcade board would print it. */
  print(s: string, col: number, row: number, colour: Colour): void {
    drawText(this.ctx, s, col * GLYPH, row * GLYPH, colour);
  }

  /** Text centred on the screen at character row `row`. */
  centre(s: string, row: number, colour: Colour): void {
    this.text(s, this.width / 2, row * GLYPH, colour, 'center');
  }

  image(img: CanvasImageSource, x: number, y: number): void {
    this.ctx.drawImage(img, Math.round(x), Math.round(y));
  }

  /** Draw with `alpha` (0-1) for the length of `fn`. */
  faded(alpha: number, fn: () => void): void {
    const before = this.ctx.globalAlpha;
    this.ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
    fn();
    this.ctx.globalAlpha = before;
  }
}

export { textWidth, GLYPH };
